import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, TreePine, MapPin, Ruler } from "lucide-react";
import { useConsultation } from "@/context/ConsultationContext";
import { Overline } from "@/components/GoldDivider";
import CtaSection from "@/components/CtaSection";
import Marquee from "@/components/Marquee";
import PhotoGrid from "@/components/PhotoGrid";
import usePhotos from "@/hooks/usePhotos";
import { IMAGES } from "@/data/content";

const HERO_LINES = ["Heirloom Cabinetry for", "Louisiana's Finest Homes"];
const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.05, delay: 0.15 + i * 0.16, ease: [0.16, 1, 0.3, 1] } }),
};
const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

const VALUES = [
  { icon: TreePine, title: "Solid Wood", body: "Real hardwood, blind dado drawers, Blum hardware. No MDF, no shortcuts." },
  { icon: Ruler, title: "3D Designed", body: "See your kitchen in full 3D before a single board is cut." },
  { icon: MapPin, title: "Made Here", body: "Built by hand in our West Monroe shop since 1998." },
];

export default function Home() {
  const { openConsultation } = useConsultation();
  const { images, loading } = usePhotos("portfolio");
  const featured = images.slice(0, 8);

  return (
    <div>
      {/* HERO - split */}
      <section className="relative min-h-screen grid lg:grid-cols-2 velvet-deep overflow-hidden" data-testid="hero-section">
        <div className="relative velvet flex items-center justify-center px-6 sm:px-12 lg:px-16 pt-28 pb-16 lg:py-24 order-2 lg:order-1">
          <div className="absolute inset-5 sm:inset-8 border border-gold/25 pointer-events-none" />
          <img src="/gold-corner.png" alt="" aria-hidden="true" className="absolute top-5 left-5 sm:top-8 sm:left-8 w-16 sm:w-24 opacity-80 pointer-events-none" />
          <img src="/gold-corner.png" alt="" aria-hidden="true" className="absolute top-5 right-5 sm:top-8 sm:right-8 w-16 sm:w-24 opacity-80 pointer-events-none scale-x-[-1]" />
          <img src="/gold-corner.png" alt="" aria-hidden="true" className="absolute bottom-5 left-5 sm:bottom-8 sm:left-8 w-16 sm:w-24 opacity-80 pointer-events-none scale-y-[-1]" />
          <img src="/gold-corner.png" alt="" aria-hidden="true" className="absolute bottom-5 right-5 sm:bottom-8 sm:right-8 w-16 sm:w-24 opacity-80 pointer-events-none scale-[-1]" />

          <div className="relative z-10 w-full max-w-lg text-center flex flex-col items-center">
            <motion.img src="/logo-crest.png" alt="West Ouachita Cabinets crest"
              initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="h-32 sm:h-40 w-auto rounded-[3px] ring-1 ring-gold/25" />
            <h1 className="font-serif text-[2rem] sm:text-4xl lg:text-[2.9rem] font-light mt-8 leading-[1.08]">
              {HERO_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden py-[0.06em]">
                  <motion.span className="block gold-gradient-text whitespace-nowrap" variants={lineReveal} custom={i} initial="hidden" animate="show">{line}</motion.span>
                </span>
              ))}
            </h1>
            <p className="text-ivory/75 text-base sm:text-lg mt-5 tracking-wide">Custom crafted in West Monroe since 1998</p>
            <div className="flex flex-col sm:flex-row gap-4 mt-9">
              <Link to="/portfolio" data-testid="hero-secondary"
                className="border border-gold/60 text-gold tracking-wider uppercase text-[0.7rem] whitespace-nowrap px-6 py-4 rounded-none hover:bg-gold hover:text-navy-deep transition-colors duration-300 text-center">
                View Our Work
              </Link>
              <button data-testid="hero-cta" onClick={openConsultation}
                className="border border-gold text-gold tracking-wider uppercase text-[0.7rem] whitespace-nowrap px-6 py-4 rounded-none hover:bg-gold hover:text-navy-deep transition-colors duration-300 flex items-center justify-center gap-2">
                Request Consultation <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div className="relative min-h-[52vh] lg:min-h-screen overflow-hidden order-1 lg:order-2">
          <img src={IMAGES.hero} alt="Custom luxury kitchen cabinetry by West Ouachita Cabinets"
            loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          <div className="hidden lg:block absolute inset-y-0 left-0 w-28 bg-gradient-to-r from-navy-deep/80 to-transparent" />
          <div className="lg:hidden absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-navy-deep to-transparent" />
        </div>
      </section>

      <Marquee items={["Solid Wood", "Made Here", "Blind Dado Drawers", "Blum Hardware", "West Monroe, LA", "3D Design"]} />

      {/* Values */}
      <section className="bg-ivory py-20 sm:py-24 px-6" data-testid="home-values">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-3 gap-8">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
              className="text-center flex flex-col items-center">
              <v.icon className="h-9 w-9 text-gold" strokeWidth={1.2} />
              <h3 className="font-serif text-2xl text-navy mt-4">{v.title}</h3>
              <p className="text-navy/70 mt-2 leading-relaxed max-w-xs">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured portfolio */}
      <section className="bg-ivory pb-24 px-6" data-testid="home-featured">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Overline>Recent Work</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-navy mt-4">Cabinetry worth showing off.</h2>
          </div>
          <PhotoGrid images={featured} loading={loading} columns={4} placeholderCount={8}
            placeholderNote="Finished install photos will showcase here. Upload them from the admin dashboard." />
          <div className="text-center mt-12">
            <Link to="/portfolio" data-testid="home-view-portfolio"
              className="inline-flex items-center gap-2 border border-navy/30 text-navy tracking-luxe uppercase text-xs px-8 py-4 hover:bg-navy hover:text-ivory transition-colors duration-300">
              View Full Portfolio <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
